import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Clock, AlertCircle, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_BASE = import.meta.env.VITE_BACKEND_URL || '';

const AppointmentSuccess = () => {
  const [paymentStatus, setPaymentStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // Get URL parameters
  const getUrlParameter = (name) => {
    name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
    const regex = new RegExp('[\\?&]' + name + '=([^&#]*)');
    const results = regex.exec(window.location.search);
    return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
  };

  const sessionId = getUrlParameter('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setError('Keine Session-ID gefunden');
      setPaymentStatus('error');
    }
  }, [sessionId]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000; // 2 seconds

    if (attempts >= maxAttempts) {
      setError('Zahlungsstatus-Überprüfung fehlgeschlagen. Bitte überprüfen Sie Ihre E-Mails.');
      setPaymentStatus('error');
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/payments/checkout/status/${sessionId}`);
      if (!response.ok) {
        throw new Error('Fehler beim Überprüfen des Zahlungsstatus');
      }

      const data = await response.json();
      setPaymentData(data);
      
      if (data.payment_status === 'paid') {
        setPaymentStatus('success');
        return;
      } else if (data.status === 'expired') {
        setError('Zahlungssession abgelaufen. Bitte versuchen Sie es erneut.');
        setPaymentStatus('error');
        return;
      }

      // If payment is still pending, continue polling
      setPaymentStatus('processing');
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setError('Fehler beim Überprüfen des Zahlungsstatus.');
      setPaymentStatus('error');
    }
  };

  const formatAmount = (amountInCents, currency) => {
    const amount = amountInCents / 100;
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(amount);
  };

  const getStatusIcon = () => {
    switch (paymentStatus) {
      case 'success':
        return <CheckCircle className="h-16 w-16 text-green-500" />;
      case 'processing':
        return <Clock className="h-16 w-16 text-blue-500 animate-spin" />;
      case 'error':
        return <AlertCircle className="h-16 w-16 text-red-500" />;
      default:
        return <Clock className="h-16 w-16 text-gray-400 animate-pulse" />;
    }
  };

  const getStatusMessage = () => {
    switch (paymentStatus) {
      case 'success':
        return {
          title: 'Zahlung erfolgreich!',
          message: 'Ihr Termin wurde erfolgreich gebucht und bezahlt.'
        };
      case 'processing':
        return {
          title: 'Zahlung wird verarbeitet...',
          message: 'Bitte warten Sie, während wir Ihre Zahlung überprüfen.'
        };
      case 'checking':
        return {
          title: 'Zahlungsstatus wird überprüft...',
          message: 'Einen Moment bitte...'
        };
      case 'error':
        return {
          title: 'Fehler bei der Zahlung',
          message: error || 'Es gab ein Problem mit Ihrer Zahlung.'
        };
      default:
        return {
          title: 'Zahlungsstatus unbekannt',
          message: 'Bitte kontaktieren Sie den Support.'
        };
    }
  };

  const statusMessage = getStatusMessage();

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-2xl mx-auto px-4">
        <Card className="text-center">
          <CardHeader className="pb-4">
            <div className="flex justify-center mb-4">
              {getStatusIcon()}
            </div>
            <CardTitle className="text-2xl">
              {statusMessage.title}
            </CardTitle>
            <p className="text-gray-600">
              {statusMessage.message}
            </p>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {paymentStatus === 'success' && paymentData && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Betrag:</span>
                    <br />
                    {formatAmount(paymentData.amount_total, paymentData.currency)}
                  </div>
                  <div>
                    <span className="font-medium">Status:</span>
                    <br />
                    <span className="text-green-600 font-medium">Bezahlt</span>
                  </div>
                  {paymentData.metadata?.customer_name && (
                    <div>
                      <span className="font-medium">Kunde:</span>
                      <br />
                      {paymentData.metadata.customer_name}
                    </div>
                  )}
                  {paymentData.metadata?.service_type && (
                    <div>
                      <span className="font-medium">Service:</span>
                      <br />
                      {paymentData.metadata.service_type}
                    </div>
                  )}
                </div>
              </div>
            )}

            {paymentStatus === 'success' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left">
                <h3 className="font-medium text-blue-900 mb-2">Was passiert als nächstes?</h3>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Sie erhalten eine Bestätigungs-E-Mail mit allen Termindetails</li>
                  <li>• Unser Team wird sich vor dem Termin bei Ihnen melden</li>
                  <li>• Sie erhalten Erinnerungen 24h und 2h vor dem Termin</li>
                  <li>• Bei Fragen kontaktieren Sie uns unter kontakt@klartech-mueritz.de</li>
                </ul>
              </div>
            )}

            <div className="flex gap-3 justify-center">
              {paymentStatus === 'success' && (
                <Button
                  onClick={() => navigate('/')}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <ArrowRight className="mr-2 h-4 w-4" />
                  Zurück zur Homepage
                </Button>
              )}
              
              {paymentStatus === 'error' && (
                <div className="space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => navigate('/appointments')}
                  >
                    Neuen Termin buchen
                  </Button>
                  <Button
                    onClick={() => window.location.href = 'mailto:kontakt@klartech-mueritz.de'}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    Support kontaktieren
                  </Button>
                </div>
              )}
            </div>

            {paymentStatus === 'processing' && (
              <div className="text-sm text-gray-500">
                <p>Dies kann einen Moment dauern...</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AppointmentSuccess;